// assets/fit-predictor.js
//
// Drives the "Fit Predictor" block on the product page. Height AND weight are
// always used, in one of two modes chosen automatically per product:
//
// 1. Chart mode (data-chart is present): the product's custom.fit_predictor_chart
//    metafield points to a "Fit Chart" metaobject with a simple line-based band
//    list, e.g.:
//      150-162 XXS
//      163-166 XS
//      ...
//      194+ XXL
//    Height picks a BASE size from the matching band. Weight then nudges that
//    base size up or down within the chart's own size list (see
//    weightAdjustment()) -- so two people of the same height but different
//    builds can land on different sizes. New fit types (new metaobject
//    entries) work automatically, no code change, and the band list itself
//    never needs to mention weight.
//
// 2. Formula mode (no chart set): falls back to a STANDARD, generic formula
//    based on BMI (weight / height^2) with a small height correction, since
//    no exact Mansour measurement chart existed for these products at build
//    time. The result is clamped to the product's custom.fit_predictor_range
//    (XXS-XXL / XS-XXL / S-XL). To tune: adjust BMI_BREAKPOINTS and
//    heightAdjustment() below, or add a Fit Chart for that product instead.
import { Component } from '@theme/component';

/**
 * @typedef {object} FitPredictorRefs
 * @property {HTMLInputElement} heightInput
 * @property {HTMLInputElement} weightInput
 * @property {HTMLElement} result
 * @property {HTMLElement} resultSize
 * @property {HTMLElement} errorMessage
 */

const SIZE_LADDER = ['XXS', 'XS', 'S', 'M', 'L', 'XL', 'XXL'];

// BMI value at/above which the prediction moves to the next size up.
// (Formula mode only -- see predictSize().)
const BMI_BREAKPOINTS = [19, 21, 23, 25, 27.5, 30];

// Maps the custom.fit_predictor_range metafield value to [minIndex, maxIndex]
// bounds within SIZE_LADDER (formula mode only).
const RANGE_TO_LADDER_BOUNDS = {
  'XXS-XXL': [0, 6],
  'XS-XXL': [1, 6],
  'S-XL': [2, 5],
};

const MIN_HEIGHT_CM = 120;
const MAX_HEIGHT_CM = 230;
const MIN_WEIGHT_KG = 30;
const MAX_WEIGHT_KG = 200;

// Matches lines like "150-162 XXS" or "194+ XXL". Lines that don't match
// (blank lines, "# comment" placeholders) are skipped.
const BAND_LINE_PATTERN = /^(\d+)\s*(?:-\s*(\d+))?\+?\s+(\S+)$/;

/**
 * @param {string} bandsText
 * @returns {{ min: number, max: number, size: string }[]}
 */
export function parseBands(bandsText) {
  if (!bandsText) return [];

  return bandsText
    .split('\n')
    .map((line) => line.trim())
    .filter((line) => line && !line.startsWith('#'))
    .map((line) => {
      const match = line.match(BAND_LINE_PATTERN);
      if (!match) return null;

      const min = Number(match[1]);
      const max = match[2] !== undefined ? Number(match[2]) : Infinity;
      const size = match[3];

      return { min, max, size };
    })
    .filter((band) => band !== null)
    .sort((a, b) => a.min - b.min);
}

/**
 * @param {{ min: number, max: number, size: string }[]} bands
 * @param {number} heightCm
 * @returns {string | null}
 */
export function sizeFromBands(bands, heightCm) {
  if (!bands.length) return null;

  const match = bands.find((band) => heightCm >= band.min && heightCm <= band.max);
  if (match) return match.size;

  // Outside every band: clamp to the nearest end rather than showing nothing.
  return heightCm < bands[0].min ? bands[0].size : bands[bands.length - 1].size;
}

/**
 * The chart's own size ladder, in the order sizes first appear across the
 * (already height-sorted) bands. Duplicate labels (e.g. a fit that caps out
 * at "XL" for several tall-height bands) only appear once.
 * @param {{ min: number, max: number, size: string }[]} bands
 * @returns {string[]}
 */
export function buildLadder(bands) {
  const seen = new Set();
  const ladder = [];
  for (const band of bands) {
    if (!seen.has(band.size)) {
      seen.add(band.size);
      ladder.push(band.size);
    }
  }
  return ladder;
}

/**
 * @param {number} bmi
 * @returns {number} index into a 7-point BMI scale (0-6)
 */
function bmiToIndex(bmi) {
  let index = 0;
  for (const breakpoint of BMI_BREAKPOINTS) {
    if (bmi < breakpoint) break;
    index += 1;
  }
  return index;
}

/**
 * How many rungs to shift on a chart's size ladder based on weight. Height
 * stays the primary signal (it picks the base size); weight only nudges that
 * base size, and only once someone is clearly leaner or heavier than an
 * average build for their height -- a normal/average BMI (roughly 20-25)
 * never shifts the size at all.
 * @param {number} heightCm
 * @param {number} weightKg
 */
function weightAdjustment(heightCm, weightKg) {
  const heightM = heightCm / 100;
  const bmi = weightKg / (heightM * heightM);

  if (bmi < 17) return -2;
  if (bmi < 20) return -1;
  if (bmi < 25) return 0;
  if (bmi < 29) return 1;
  return 2;
}

/**
 * Chart mode: height picks a base size from the bands, weight nudges it up
 * or down within that same chart's size list.
 * @param {{ min: number, max: number, size: string }[]} bands
 * @param {number} heightCm
 * @param {number} weightKg
 * @returns {string | null}
 */
export function sizeFromChart(bands, heightCm, weightKg) {
  if (!bands.length) return null;

  const ladder = buildLadder(bands);
  const baseSize = sizeFromBands(bands, heightCm);
  const baseIndex = ladder.indexOf(baseSize);

  if (baseIndex === -1) return baseSize;

  const index = Math.min(Math.max(baseIndex + weightAdjustment(heightCm, weightKg), 0), ladder.length - 1);
  return ladder[index];
}

/**
 * Taller-than-average frames tend to need a size up for sleeve/inseam length;
 * shorter frames a size down, independent of BMI. Formula mode only.
 * @param {number} heightCm
 */
function heightAdjustment(heightCm) {
  if (heightCm >= 190) return 1;
  if (heightCm < 165) return -1;
  return 0;
}

/**
 * Formula mode (no chart assigned to the product).
 * @param {number} heightCm
 * @param {number} weightKg
 * @param {string} rangeKey
 * @returns {string}
 */
export function predictSize(heightCm, weightKg, rangeKey) {
  const bmi = weightKg / ((heightCm / 100) * (heightCm / 100));

  let index = bmiToIndex(bmi) + heightAdjustment(heightCm);

  const [min, max] = RANGE_TO_LADDER_BOUNDS[rangeKey] || RANGE_TO_LADDER_BOUNDS['S-XL'];
  index = Math.min(Math.max(index, min), max);

  const size = SIZE_LADDER[index];
  if (!size) throw new Error(`Fit Predictor: no size resolved for index ${index}`);
  return size;
}

/**
 * @extends Component<FitPredictorRefs>
 */
class FitPredictor extends Component {
  calculate() {
    const { heightInput, weightInput, result, resultSize, errorMessage } = this.refs;

    const height = Number(heightInput.value);
    const weight = Number(weightInput.value);

    errorMessage.hidden = true;
    result.hidden = true;

    const heightValid = Number.isFinite(height) && height >= MIN_HEIGHT_CM && height <= MAX_HEIGHT_CM;
    const weightValid = Number.isFinite(weight) && weight >= MIN_WEIGHT_KG && weight <= MAX_WEIGHT_KG;

    if (!heightValid || !weightValid) {
      errorMessage.hidden = false;
      return;
    }

    const chartText = this.dataset.chart || '';
    const bands = parseBands(chartText);

    if (bands.length > 0) {
      const size = sizeFromChart(bands, height, weight);
      resultSize.textContent = size ?? '';
      result.hidden = false;
      return;
    }

    if (chartText.trim()) {
      // A chart was assigned to this product but has no usable rows yet
      // (e.g. a placeholder chart waiting on real measurements).
      errorMessage.hidden = false;
      return;
    }

    const rangeKey = this.dataset.range || 'S-XL';
    const size = predictSize(height, weight, rangeKey);

    resultSize.textContent = size;
    result.hidden = false;
  }
}

if (!customElements.get('fit-predictor-component')) {
  customElements.define('fit-predictor-component', FitPredictor);
}
